import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listMemberships,
  getMembership,
  createMembership,
  updateMembership,
  listMembershipsForPatient,
  listFutureRequestsForMembership,
} from "api/memberships";
import { membershipFromJson } from "schemas/membership";
import { queryKeys } from "./keys";
import { CACHE } from "config/cachePolicy";

export async function fetchMembershipsPage({
  filters = {},
  data = { results: [] },
  limit = 30,
} = {}) {
  const response = await listMemberships(filters, { limit });
  return {
    ...(data || { results: [] }),
    ...response.data,
    results: [
      ...(data?.results || []),
      ...(response.data.results || []).map(membershipFromJson),
    ],
  };
}

export function useMembershipsList(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.memberships.list(filters),
    queryFn: () => fetchMembershipsPage({ filters }),
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export function useMembership(uuid, options = {}) {
  return useQuery({
    queryKey: queryKeys.memberships.detail(uuid),
    queryFn: async () => {
      const response = await getMembership(uuid);
      return membershipFromJson(response.data);
    },
    enabled: !!uuid,
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export async function fetchMembership(membershipUuid) {
  const response = await getMembership(membershipUuid);
  if (!response?.data) {
    throw new Error(response);
  }
  return membershipFromJson(response.data);
}

export async function createMembershipRequest(payload) {
  const response = await createMembership(payload);
  return membershipFromJson(response.data);
}

export async function updateMembershipRequest({ membershipUuid, payload }) {
  await updateMembership(membershipUuid, payload);
  return fetchMembership(membershipUuid);
}

export async function fetchMembershipsForPatient(payload) {
  const response = await listMembershipsForPatient(payload);
  return response?.data?.map(membershipFromJson);
}

export async function fetchFallOutOfMembershipServiceRequests({
  membershipUuid,
  payload,
} = {}) {
  const response = await listFutureRequestsForMembership({
    membershipUuid,
    payload,
  });
  return response?.data;
}

export function useCreateMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMembershipRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.memberships.all });
    },
  });
}

export function useUpdateMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMembershipRequest,
    onSuccess: (data, { membershipUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.memberships.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.detail(membershipUuid),
      });
      if (data) {
        queryClient.setQueriesData(
          { queryKey: queryKeys.memberships.all },
          (old) => {
            if (!old?.results) return old;
            return {
              ...old,
              results: old.results.map((item) =>
                item.uuid === data.uuid ? data : item,
              ),
            };
          },
        );
      }
    },
  });
}

export function useMembershipsForPatient(payload, options = {}) {
  return useQuery({
    queryKey: queryKeys.memberships.forPatient(payload),
    queryFn: () => fetchMembershipsForPatient(payload),
    enabled: !!payload,
    staleTime: CACHE.LIVE,
    ...options,
  });
}
